import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { auth } from "@lyder-talk/core/lib/auth-client";

/**
 * Landing page for every emailed link that only needs the address confirmed —
 * sign-up confirmation and an email-change confirmation.
 *
 * The token is redeemed by `auth.confirmEmail()` against the API. The previous
 * implementation waited for `consumeAuthEmailToken()` to establish a session,
 * which was the old GoTrue shape: confirmation and email change never mint a
 * session, so even a valid link could only end in "invalid".
 */
const ConfirmEmail = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const [statusMessage, setStatusMessage] = useState("Confirming your email...");
  const hasProcessedToken = useRef(false);

  const token = searchParams.get("token_hash") ?? searchParams.get("token") ?? "";
  const linkType = searchParams.get("type");

  useEffect(() => {
    if (hasProcessedToken.current) return;
    hasProcessedToken.current = true;

    const finish = async () => {
      if (!token) {
        toast({
          title: "Invalid Link",
          description: "This confirmation link is missing its token. Please request a new one.",
          variant: "destructive",
        });
        navigate("/auth/signin", { replace: true });
        return;
      }

      try {
        const { data, error } = await auth.confirmEmail(token);
        if (error) throw new Error(error.message);

        const changedEmail = data?.type === "email_change" || linkType === "email_change";

        if (changedEmail) {
          setStatusMessage("Email updated");
          toast({
            title: "Email updated",
            description: data?.email
              ? `Your account now signs in as ${data.email}.`
              : "Your email address has been changed successfully.",
          });
          // The access token still carries the old address; rotate it so the
          // profile header stops showing the address that no longer signs in.
          // A no-op on the device that requested the change but never had a
          // session (the link can be opened anywhere).
          await auth.refresh();
        } else {
          setStatusMessage("Email confirmed");
          toast({
            title: "Email confirmed",
            description: "Welcome to Lyder Talk! You're all set — sign in to start practising.",
          });
        }

        // A confirmation link is used signed out; an email-change link comes
        // from a signed-in settings page. Send each back where it belongs.
        const {
          data: { session },
        } = await auth.getSession();
        navigate(session ? "/" : "/auth/signin", { replace: true });
      } catch (err) {
        console.error("Error confirming email link:", err);
        toast({
          title: "Invalid Link",
          description:
            err instanceof Error && err.message
              ? err.message
              : "This confirmation link is invalid. Please request a new one.",
          variant: "destructive",
        });
        navigate("/auth/signin", { replace: true });
      }
    };

    finish();
  }, [navigate, toast, token, linkType]);

  return (
    <div className="flex flex-col items-center gap-4 py-12">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      <p className="text-muted-foreground animate-pulse">{statusMessage}</p>
    </div>
  );
};

export default ConfirmEmail;
